'use client';

import { useEffect } from 'react';

/**
 * Shared scroll-reveal animation for the public marketing pages (homepage +
 * the design showcase pages). Replaces the ~10 copy-pasted IntersectionObserver
 * effects that previously lived in each page (docs/82 · DP2 + DP3).
 *
 * The animation styles it drives live once in `globals.css`
 * (`.observe-me` / `.reveal-armed` / `.animate-in` + the `reveal*` keyframes).
 *
 * Why it is built this way — the failure modes DP2 names:
 *  - **No blank page if JS fails.** Content is VISIBLE by default; there is no
 *    `opacity: 0` baked into the static CSS. The hidden state (`.reveal-armed`)
 *    is added *only by this hook*, which runs only after a successful client
 *    mount. If the JS bundle never loads or hydration throws, nothing is ever
 *    hidden — the server-rendered content stays fully readable (also the
 *    strongest SEO signal: crawlers see visible content in the raw HTML).
 *  - **Reduced motion (WCAG 2.3.3).** If the OS requests reduced motion, the
 *    hook does nothing — every element simply stays visible, no animation.
 *  - **No flash.** Only elements *below the fold* at mount are hidden + animated
 *    on scroll. Anything already on screen is left visible (animating it would
 *    require briefly hiding it first — a visible flicker, worst on slow links).
 *    Trade-off: initially-visible `.observe-me` blocks appear without an
 *    entrance animation; everything the user scrolls to still animates in.
 */
export function useScrollReveal() {
  useEffect(() => {
    // Respect "reduce motion": leave everything visible, run no animation.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const elements = Array.from(
      document.querySelectorAll<HTMLElement>('.observe-me')
    );
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            // Stagger elements that enter together (e.g. a grid row).
            window.setTimeout(() => {
              el.classList.remove('reveal-armed');
              el.classList.add('animate-in');
            }, index * 100);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const viewportHeight = window.innerHeight;
    elements.forEach((el) => {
      // Only arm (hide + observe) elements fully below the fold. Off-screen, so
      // hiding them now is invisible to the user — they animate in on scroll.
      // Elements already in/straddling the viewport stay visible (no flash).
      if (el.getBoundingClientRect().top >= viewportHeight) {
        el.classList.add('reveal-armed');
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);
}
